import { Controller } from "@hotwired/stimulus";
import { actionSource } from "../utils/action_source";
import { isAriaDisabled } from "../utils/aria_disabled";
import { hasModifierChord, isReservedArrowChord } from "../utils/arrow_step";
import { ClickVerdicts } from "../utils/click_verdicts";
import { claimsWhileFocusWithin, EscapeLayer } from "../utils/escape_layer";
import { ownerOf, ownersOf } from "../utils/event_owner";
import { ListenerSet } from "../utils/listener_set";
import { isRtl } from "../utils/logical_scroll";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";
import { RovingTabindex } from "../utils/roving_tabindex";
import { SafeTimeout } from "../utils/safe_timeout";
import type { StateReason } from "../utils/state_reason";
import { targetSelector } from "../utils/target_selector";
import { findTypeaheadMatch, isTypeaheadKey, Typeahead } from "../utils/typeahead";

/** Where to land focus when a menu opens. */
type OpenFocus = "first" | "last";

/**
 * Attributes whose runtime change flips a top item's navigability, watched so the
 * single Tab stop survives a consumer disabling or hiding the item that held it.
 *
 * `tabindex` is deliberately absent — this controller writes it, and observing it
 * would feed back into the observer. `aria-disabled` is absent for a different
 * reason: per the APG it keeps an item focusable and only blocks activation, so
 * it never changes *which* top can hold the Tab stop.
 */
const STATE_ATTRIBUTES = ["disabled", "hidden"];

/**
 * Headless, accessible **menubar** behavior.
 *
 * Markup contract (identifier: `stimeo--menubar`):
 *   <div data-controller="stimeo--menubar" role="menubar" aria-label="Main">
 *     <button role="menuitem" aria-haspopup="menu" aria-expanded="false"
 *             aria-controls="m-file" data-stimeo--menubar-target="top"
 *             data-action="click->stimeo--menubar#toggle
 *                          keydown->stimeo--menubar#onTopKeydown">File</button>
 *     <ul id="m-file" role="menu" data-stimeo--menubar-target="menu" hidden>
 *       <li role="none">
 *         <button role="menuitem" tabindex="-1" data-stimeo--menubar-target="item"
 *                 data-action="click->stimeo--menubar#activate
 *                              keydown->stimeo--menubar#onItemKeydown">New</button>
 *       </li>
 *     </ul>
 *     <!-- more top items + menus -->
 *   </div>
 *
 * Implements the WAI-ARIA APG **Menubar** pattern (single level): the top items
 * form one Tab stop via roving tabindex and the arrow keys move between them;
 * `ArrowDown`/`Enter`/`Space` open a menu (`ArrowUp` opens it at the last item),
 * the arrow keys then move within the menu, and pressing `ArrowLeft`/`ArrowRight`
 * while a menu is open jumps to the adjacent top menu. `Escape` closes and
 * returns focus to the owning top item — while a menu is open the menubar is a
 * layer on the shared `EscapeLayer` stack, claiming a press only while
 * focus is inside the controller or fell to the body, so one keypress closes
 * exactly one layer. `Tab` and an outside click close.
 *
 * @remarks
 * Behavior only. Each top item↔menu pair is linked by `aria-controls`/`id` (not by
 * position), so *pairing* does not depend on the markup order. Where the order does
 * show is native `Tab`/`Shift+Tab`: the browser walks the DOM, so `Shift+Tab` out of
 * a menu lands on the owning top item only while the menu follows it — write the
 * conventional owner-then-menu order to get the destinations described here. Menu
 * placement and viewport-edge collision are out of scope — static placement is the
 * consumer's CSS, and dynamic placement is delegated to the opt-in
 * `stimeo-ui/positioning` module (never imported here, keeping the core
 * zero-dependency). Roving mechanics across the top items are delegated to
 * `RovingTabindex`.
 *
 * Focus-restoration contract: closing via `Escape` or an item activation returns
 * focus to the owning top item; `Tab` and an outside click deliberately do **not**
 * — they close where the user is already heading, and yanking focus back would
 * fight the pointer/Tab move.
 *
 * Disabledness follows the APG's two-attribute split, which is the consumer's way
 * of saying which one they mean:
 * - **`hidden` / natively `disabled`** — removed from the roving set entirely, so
 *   the lone Tab stop never lands on an unfocusable top item (which would drop the
 *   whole menubar out of the Tab order) and the arrow keys never stall on it.
 * - **`aria-disabled="true"`** — still reached by the arrow keys ("disabled menu
 *   items are focusable but cannot be activated"), so a screen-reader user can
 *   discover it; only *activation* is suppressed. ARIA extends the state to every
 *   focusable descendant, so an entry inside an element carrying it (its menu, the
 *   menubar, anything around it) counts as `aria-disabled` too. Clicks on such an
 *   entry — including the one a browser synthesizes for Enter/Space — are
 *   swallowed before the handlers inside the menubar element run, and such a
 *   top never opens its menu, since opening the popup is activation. Moving onto it
 *   still closes whatever menu was open, which the APG makes unconditional for a
 *   horizontal move.
 *
 * Under all three, {@link MenubarController.toggle} and
 * {@link MenubarController.activate} refuse the entry whatever they are called
 * with — a DOM event of any type or an explicit element — and a refusal changes
 * nothing and reports nothing. Entries can nest, so the judgement covers every
 * entry that holds what the call lands on — the event's target, or the element
 * passed — and the call is refused when any of them refuses: what lands inside an
 * inner entry counts as landing on it, whichever entry the action is bound to. A
 * click is judged once, as it starts: a capture listener on the menubar element
 * takes the verdict before the handlers inside that element run, and the action
 * that click reaches keeps it, so a consumer command that marks its own entry
 * `disabled` or `aria-disabled` during the click still gets the close or open it
 * started; a top whose menu that click opened and that the command leaves natively
 * `disabled` is then closed again, without a report, by the reconciliation that
 * follows. The action keeps the verdict only for an entry that held the click's
 * target when it was taken: an entry it was not taken for — a handler that ran
 * first, such as a capture listener on `document`, moved the target — is judged
 * when the action runs, as is any other event and an explicit element.
 *
 * Arrow directions are logical: `ArrowRight` moves to the next top item under LTR
 * and to the previous one under RTL, on the bar and from inside an open menu. A top
 * item may also be a **plain command** with no `aria-controls` at all: moving to it
 * behaves the same way — the open menu closes and it takes the roving focus. A top
 * item whose `aria-controls` names no menu target is broken markup instead, and the
 * open/closed state is left alone rather than acted on by guesswork.
 *
 * A menu with no navigable items still opens (its `aria-expanded` flips and focus
 * stays on the top item) so a consumer that fills a menu asynchronously keeps
 * working; `Escape` or a second click closes it. While such a menu is
 * structurally empty the consumer **must** carry `aria-busy="true"` on it and drop
 * that once the items land — `role="menu"` requires owned `menuitem`s, and this
 * controller cannot tell "still loading" from "nothing to show", so it never
 * infers the state. A menu whose items exist but are all inert is not busy.
 *
 * Runtime DOM edits (Turbo Streams, a consumer swapping menus in) are reconciled
 * from the live targets: expanded flags, menu visibility, the single Tab stop, and
 * Escape-stack membership are re-derived whenever a top/menu target is added or
 * removed, and whenever `disabled`/`hidden` changes under the menubar.
 *
 * Each move of a menu's open state is reported: `stimeo--menubar:open` and
 * `stimeo--menubar:close` dispatch
 * `{ reason: StateReason, index: number, menu: HTMLElement }` — `index` is the
 * top item's position in `topTargets` — after the state attributes are written.
 * Both are informational, so neither is cancelable. Moving along the menubar
 * with a menu open reports the outgoing `close` before the incoming `open`. A
 * call that leaves a menu where it already was, the normalization in
 * {@link connect}, the reconciliation that follows target churn, and
 * {@link disconnect} are all silent.
 */
export class MenubarController extends Controller<HTMLElement> {
  static override targets = ["top", "menu", "item"];
  static actions = ["activate", "onItemKeydown", "onTopKeydown", "toggle"] as const;
  static events = ["close", "open"] as const;

  declare readonly topTargets: HTMLButtonElement[];
  declare readonly menuTargets: HTMLElement[];
  declare readonly itemTargets: HTMLButtonElement[];

  /** Roving tabindex across the top-level menuitems (one Tab stop). */
  readonly #roving = new RovingTabindex(() => this.topTargets);
  /** Typeahead query and its idle-reset timer (scoped to the open menu). */
  readonly #typeahead = new Typeahead();
  readonly #timers = new SafeTimeout();

  /** Escape-stack membership while a menu is open; the shared resolver dismisses via it. */
  readonly #escapeLayer = new EscapeLayer();
  /** Whether {@link #escapeLayer} is currently on the stack (see {@link #syncEscapeLayer}). */
  #layerActive = false;

  /** Watches `disabled`/`hidden` under the menubar; `null` while disconnected. */
  #observer: MutationObserver | null = null;
  /**
   * Live between `connect()` and `disconnect()`. Stimulus reports the *initial*
   * targets before `connect()` and re-reports them as disconnected afterwards;
   * gating on this keeps the target callbacks from clobbering the authored Tab
   * stop on mount and from resurrecting one after teardown.
   */
  #connected = false;

  /** Whether state moves are reported: set once `connect()` settled the baseline. */
  #reporting = false;

  /** The element inside the menubar that last took DOM focus, if any. */
  #focused: HTMLElement | null = null;

  /** The activation verdict the capture pass took for a click, with the entries it judged. */
  readonly #clickVerdicts = new ClickVerdicts();

  /**
   * Records where DOM focus sits inside the menubar. `focusin` bubbles, so one
   * listener covers the top items and everything nested in their menus. The
   * record is needed because a top or menu becomes unreachable *before* this
   * controller hears about it, by which point the browser has already reset
   * `document.activeElement`.
   */
  readonly #onFocusIn = (event: FocusEvent): void => {
    this.#focused = event.target as HTMLElement | null;
  };

  /**
   * Forgets the record once focus genuinely lands on something else, so a later
   * mutation cannot pull focus back in. A `relatedTarget` of `null` is the case
   * that must be kept: it is what "focus went nowhere" looks like, which is
   * exactly the state the rescue exists for.
   */
  readonly #onFocusOut = (event: FocusEvent): void => {
    const next = event.relatedTarget as HTMLElement | null;
    if (next && !this.element.contains(next)) this.#focused = null;
  };

  readonly #morph = new MorphRenderWatcher(() => this.#reconcile());

  /** The listeners of one connection, released together on `disconnect()`. */
  readonly #listeners = new ListenerSet();

  /**
   * Establishes the closed baseline and the single tab stop: keep an existing tab
   * stop when it is still navigable (so a Turbo cache restore preserves the user's
   * position), else fall back to the first navigable top item.
   *
   * The outside-click listener is registered in the **capture** phase: in the
   * bubble phase an inside handler that removes its own click target detaches the
   * node before `contains()` runs, so an *inside* click would read as outside and
   * close the menu.
   */
  override connect(): void {
    this.#morph.observe(this.element);
    this.#closeAllMenus("api");
    this.#reconcile();
    this.#listeners.add(this.element, "click", this.#onClickCapture, { capture: true });
    this.#listeners.add(this.element, "focusin", this.#onFocusIn);
    this.#listeners.add(this.element, "focusout", this.#onFocusOut);
    this.#listeners.add(document, "click", this.#onOutsideClick, { capture: true });
    if (typeof MutationObserver !== "undefined") {
      this.#observer = new MutationObserver(() => this.#morph.schedule());
      this.#observer.observe(this.element, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: STATE_ATTRIBUTES,
      });
    }
    this.#connected = true;
    this.#reporting = true;
  }

  /** Removes the listeners, stack membership, and any pending timer (typeahead / Tab close). */
  override disconnect(): void {
    this.#morph.disconnect();
    this.#connected = false;
    this.#reporting = false;
    this.#escapeLayer.deactivate();
    this.#layerActive = false;
    this.#listeners.dispose();
    this.#focused = null;
    this.#observer?.disconnect();
    this.#observer = null;
    // The typeahead owns its own timer registry, so `#timers.clearAll()` does not
    // reach its pending reset — drop it explicitly or it outlives the element.
    this.#typeahead.reset();
    this.#timers.clearAll();
  }

  /**
   * A top item added at runtime is dropped out of the Tab sequence first — a fresh
   * `<button>` is tabbable by default, which would leave the menubar with two Tab
   * stops — before the lone stop is re-established.
   */
  topTargetConnected(top: HTMLButtonElement): void {
    if (!this.#connected) return;
    top.tabIndex = -1;
    this.#reconcile();
  }

  /**
   * Removing a top item can strand two things: the Tab stop it held, and the menu
   * it owned (which nothing could close afterwards).
   *
   * The departing element is handed back in a neutral state first. A target can
   * leave without leaving the document — a morph that only drops the
   * `data-*-target` token keeps the node — and it would then sit in the page
   * carrying this controller's `tabindex="0"` (a second Tab stop next to the one
   * re-established below) and an `aria-expanded="true"` nothing can collapse.
   * When the node really is gone these writes are harmless no-ops.
   */
  topTargetDisconnected(top: HTMLButtonElement): void {
    if (!this.#connected) return;
    top.tabIndex = -1;
    if (this.#isExpanded(top)) top.setAttribute("aria-expanded", "false");
    this.#reconcile();
  }

  /** See {@link MenubarController.menuTargetDisconnected}. */
  menuTargetConnected(): void {
    if (!this.#connected) return;
    this.#reconcile();
  }

  /**
   * A menu removed while open leaves its owner claiming `aria-expanded="true"` for
   * a popup that no longer exists, and the menubar holding an Escape layer that
   * would swallow presses meant for something else.
   *
   * As with {@link MenubarController.topTargetDisconnected}, the departing menu is
   * closed first: a token-only removal leaves a visible popup in the page that no
   * key and no click can dismiss any more.
   */
  menuTargetDisconnected(menu: HTMLElement): void {
    if (!this.#connected) return;
    if (!menu.hidden) menu.hidden = true;
    this.#reconcile();
  }

  /**
   * Toggles an owned top item's menu from an action or explicit element.
   *
   * A top that is `hidden`, natively `disabled` or `aria-disabled` (itself or
   * through an ancestor) is refused on every route: nothing opens or closes — not
   * even another open menu — and focus, the Tab stop and the state events stay as
   * they were, as they do for a click on such a top. So is a top whose target lies
   * inside a nested entry that refuses. A click keeps the verdict taken as it started,
   * before the handlers inside the menubar element ran, when the top held its
   * target then; a top that verdict was not taken for, and anything else, is judged
   * now.
   */
  toggle(source: Event | HTMLElement): void {
    const { event, host, origin, reason } = actionSource(source);
    const top = host?.closest<HTMLButtonElement>(targetSelector(this.identifier, "top"));
    if (!top || !this.topTargets.includes(top)) return;
    if (origin && origin.closest(`[data-controller~="${this.identifier}"]`) !== this.element)
      return;
    if (this.#refuses(top, event, origin)) return;
    const moveFocus = event !== null || this.element.contains(document.activeElement);
    if (this.#isExpanded(top)) {
      this.#closeMenu(top, reason);
    } else {
      this.#openMenu(top, "first", reason, moveFocus, true);
    }
  }

  /** Keyboard handling while focus is on a top item. */
  onTopKeydown(event: KeyboardEvent): void {
    // A descendant widget that already claimed the key (a grabbed drag handle, an
    // inline editor) must not ALSO move the roving focus — composition depends on
    // this yield.
    if (event.defaultPrevented) return;
    if (isReservedArrowChord(event)) return;
    if ((event.key === "Home" || event.key === "End") && hasModifierChord(event)) return;
    const tops = this.#navigableTops;
    const index = tops.indexOf(event.currentTarget as HTMLButtonElement);
    if (index === -1) return;
    const length = tops.length;
    // Whether a menu is currently open governs whether horizontal moves *open*
    // the adjacent menu or merely move the roving focus between top items.
    const anyOpen = this.#isAnyOpen;

    // Logical, not physical. APG defines these as "next / previous item", and
    // says a vertical arrangement swaps in Down/Up for the same meaning — so the
    // pair is one axis's spelling of an order, and the order reverses with the
    // writing direction. Read from the controller element: the container is what
    // lays the items out, and a child may carry its own `dir` (an LTR input inside
    // an RTL form is ordinary authoring).
    const step = isRtl(this.element) ? -1 : 1;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        this.#gotoTop(tops[(index + step + length) % length], anyOpen, "user");
        break;
      case "ArrowLeft":
        event.preventDefault();
        this.#gotoTop(tops[(index - step + length) % length], anyOpen, "user");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.#openMenu(tops[index], "first", "user");
        break;
      case "ArrowUp":
        event.preventDefault();
        this.#openMenu(tops[index], "last", "user");
        break;
      case "Home":
        event.preventDefault();
        this.#gotoTop(tops[0], anyOpen, "user");
        break;
      case "End":
        event.preventDefault();
        this.#gotoTop(tops[length - 1], anyOpen, "user");
        break;
      case "Tab":
        // Focus is leaving the menubar: close, but let the browser's own Tab move
        // run first (see #closeMenusSoon).
        this.#closeMenusSoon();
        break;
      default:
        break;
    }
    // Enter/Space are intentionally left to the native button click, which runs
    // toggle(): it opens the menu at its first item, or closes it when that menu
    // is already open. Handling them here would double-fire.
    // Escape is owned by the shared EscapeLayer resolver (see #dismissOpenMenu).
  }

  /** Keyboard handling while focus is on a menu item. */
  onItemKeydown(event: KeyboardEvent): void {
    // See onTopKeydown: a key a descendant already consumed is not ours.
    if (event.defaultPrevented) return;
    if (isReservedArrowChord(event)) return;
    if ((event.key === "Home" || event.key === "End") && hasModifierChord(event)) return;
    const item = event.currentTarget as HTMLButtonElement;
    const menu = item.closest<HTMLElement>("[role='menu']");
    if (!menu) return;
    const items = this.#itemsIn(menu);
    // -1 when the key came from an item outside this menu's navigable set:
    // ArrowDown then lands on the first item, ArrowUp on the second-to-last.
    const index = items.indexOf(item);
    const length = items.length;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.#focusAt(items, (index + 1) % length);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.#focusAt(items, (index - 1 + length) % length);
        break;
      case "Home":
        event.preventDefault();
        this.#focusAt(items, 0);
        break;
      case "End":
        event.preventDefault();
        this.#focusAt(items, length - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        this.#moveToAdjacentMenu(menu, isRtl(this.element) ? -1 : 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        this.#moveToAdjacentMenu(menu, isRtl(this.element) ? 1 : -1);
        break;
      case "Tab":
        // Per APG, Tab closes the menubar but lets focus move on naturally.
        this.#closeMenusSoon();
        break;
      default:
        if (isTypeaheadKey(event)) {
          event.preventDefault();
          this.#typeaheadTo(items, index, event.key);
        }
        break;
    }
    // Enter/Space activate the item via its native button click → activate().
  }

  /**
   * Closes an activated item's menu, returning focus only from inside on API calls.
   *
   * An item that is `hidden`, natively `disabled` or `aria-disabled` (itself or
   * through an ancestor) is refused on every route: its menu stays open, focus and
   * the Tab stop stay where they are, and no `close` is reported. So is an item
   * whose target lies inside a nested entry that refuses. A click keeps the verdict
   * taken as it started, before the handlers inside the menubar element ran,
   * when the item held its target then; an item that verdict was not taken for, and
   * anything else, is judged now.
   */
  activate(source: Event | HTMLElement): void {
    const { event, host, origin } = actionSource(source);
    const item = host?.closest<HTMLButtonElement>(targetSelector(this.identifier, "item"));
    if (!item || !this.itemTargets.includes(item)) return;
    if (origin && origin.closest(`[data-controller~="${this.identifier}"]`) !== this.element)
      return;
    if (this.#refuses(item, event, origin)) return;
    const moveFocus = event !== null || this.element.contains(document.activeElement);
    const menu = item.closest<HTMLElement>("[role='menu']");
    const top = this.#topFor(menu);
    this.#closeAllMenus("select");
    this.#focusTop(
      top,
      moveFocus && (event !== null || this.element.contains(document.activeElement)),
    );
  }

  /** Moves the roving focus to `destination`, opening its menu when one was open. */
  #gotoTop(destination: HTMLButtonElement | undefined, reopen: boolean, reason: StateReason): void {
    if (!destination) return;
    if (reopen) {
      this.#openMenu(destination, "first", reason);
    } else {
      // The roving index is resolved against the full target list, since that is
      // what carries the tabindex bookkeeping.
      this.#roving.setActive(this.topTargets.indexOf(destination), { focus: true });
    }
  }

  /**
   * Opens `opener`'s menu (closing others) and focuses its first/last item.
   *
   * Three kinds of top item do not open a menu:
   * - **`aria-disabled`** (itself or through an ancestor), unless `judged` —
   *   focusable but never activated, and opening a popup is activation.
   * - **plain command** (no `aria-controls` at all), `judged` or not — a legitimate
   *   top item that simply has no popup.
   * - **dangling `aria-controls`** (names a menu that is not a target), `judged` or
   *   not — broken markup.
   *
   * The first two still take the roving focus, and any open menu closes. On a
   * horizontal move the APG closes the open menu, moves focus to the adjacent
   * menubar item, and opens that item's submenu only when it has one. A plain
   * command has none, and an `aria-disabled` item is "focusable but cannot be
   * activated" while opening a popup is activation, so for both only the opening
   * half is skipped. Leaving the old menu open would strand a popup that focus has
   * left.
   *
   * `judged` is set by {@link MenubarController.toggle}, which has already decided
   * that `opener` is activated — for a click, as the click started — so its
   * `aria-disabled` is not read again here.
   *
   * The dangling case instead leaves the open/closed state and focus untouched
   * rather than closing everything and dropping focus to the body — but only on
   * this "open a menu" path. A dangling top is still an ordinary roving
   * destination while nothing is open.
   */
  #openMenu(
    opener: HTMLButtonElement | null | undefined,
    focus: OpenFocus,
    reason: StateReason,
    moveFocus = true,
    judged = false,
  ): void {
    if (!opener) return;
    // Resolve the menu *before* touching any state, so the dangling case below
    // can bail out without having changed anything.
    const menu = this.#menuFor(opener);
    if (!menu || (!judged && isAriaDisabled(opener))) {
      // Broken markup (an `aria-controls` that resolves to nothing) is the one
      // case that must not act on a guess.
      if (menu || !opener.hasAttribute("aria-controls")) {
        this.#closeAllMenus(reason);
        this.#focusTop(
          opener,
          moveFocus && (reason !== "api" || this.element.contains(document.activeElement)),
        );
      }
      return;
    }
    // A reopen must discard a pending Tab close, or that stale task would slam
    // the freshly opened menu shut on the next tick.
    this.#timers.clearAll();
    const was = this.#isExpanded(opener);
    // The blanket close also rewrites this top's own attributes, so its move is
    // reported from here instead — a reopen of the menu that is already open
    // leaves the state where it was and says nothing.
    this.#closeAllMenus(reason, opener);
    menu.hidden = false;
    opener.setAttribute("aria-expanded", "true");
    if (!was) this.#report("open", opener, menu, reason);
    // A subscriber may close it again from the handler above. The layer and the
    // focus move below belong to a menu that is open; against a hidden one they
    // strand the Escape layer and put focus where nothing is visible.
    if (!this.#isExpanded(opener)) return;
    this.#escapeLayer.activate(document, {
      onDismiss: () => this.#dismissOpenMenu(),
      claims: claimsWhileFocusWithin(this.element),
    });
    // Opening always re-registers, which puts this layer back on top of the
    // stack — the menubar is the innermost thing the user just interacted with.
    this.#layerActive = true;
    this.#roving.setActive(this.topTargets.indexOf(opener));
    const items = this.#itemsIn(menu);
    if (moveFocus && (reason !== "api" || this.element.contains(document.activeElement))) {
      this.#focusAt(items, focus === "first" ? 0 : items.length - 1);
    }
  }

  /**
   * Hides `top`'s menu and reflects the collapsed state. A top item that controls
   * no menu (a plain command in an otherwise popup-bearing menubar) is left alone
   * — stamping `aria-expanded="false"` on it would announce a popup it lacks.
   */
  #closeMenu(top: HTMLButtonElement | null, reason: StateReason, silent = false): void {
    if (!top) return;
    const menu = this.#menuFor(top);
    if (!menu) return;
    const was = this.#isExpanded(top);
    menu.hidden = true;
    top.setAttribute("aria-expanded", "false");
    if (was && !silent) this.#report("close", top, menu, reason);
    this.#syncEscapeLayer();
  }

  /**
   * Closes every menu and resets the typeahead query. `except` still has its
   * attributes rewritten — the caller is about to reopen it — but its move is
   * left for that caller to report.
   */
  #closeAllMenus(reason: StateReason, except: HTMLButtonElement | null = null): void {
    for (const top of this.topTargets) this.#closeMenu(top, reason, top === except);
    this.#typeahead.reset();
  }

  /** Names the menu that moved, so a subscriber does not have to re-derive it. */
  #report(
    name: "close" | "open",
    top: HTMLButtonElement,
    menu: HTMLElement,
    reason: StateReason,
  ): void {
    if (!this.#reporting) return;
    const detail = { reason, index: this.topTargets.indexOf(top), menu };
    if (name === "open") this.dispatch("open", { detail, cancelable: false });
    else this.dispatch("close", { detail, cancelable: false });
  }

  /**
   * Closes every menu on the next task instead of synchronously.
   *
   * Used by `Tab`: closing right away removes the focused element before the
   * browser performs its own Tab move, which can restart traversal at the
   * document head and lose the user's place in the Tab order.
   */
  #closeMenusSoon(): void {
    if (!this.#isAnyOpen) return;
    this.#timers.clearAll();
    this.#timers.set(() => this.#closeAllMenus("focus"), 0);
  }

  /**
   * Escape path, invoked by the shared resolver: pressed inside an open menu it
   * closes that menu and returns focus to its top item (the APG behavior);
   * pressed while focus is on a top item (or fell to the body) it closes every
   * menu without moving focus.
   */
  #dismissOpenMenu(): void {
    const menu = ownerOf(this.menuTargets, document.activeElement);
    if (menu) {
      const top = this.#topFor(menu);
      this.#closeMenu(top, "escape");
      this.#focusTop(top);
      return;
    }
    this.#closeAllMenus("escape");
  }

  /** Opens the menu of the navigable top item `delta` steps from `menu`'s owner. */
  #moveToAdjacentMenu(menu: HTMLElement, delta: number): void {
    const ownerTop = this.#topFor(menu);
    if (!ownerTop) return;
    const tops = this.#navigableTops;
    const current = tops.indexOf(ownerTop);
    if (current === -1) return;
    const next = (current + delta + tops.length) % tops.length;
    this.#openMenu(tops[next], "first", "user");
  }

  /**
   * Closes when a click lands outside the controller's element. Focus is left on
   * whatever the user clicked — see the focus-restoration contract in the class
   * docs.
   */
  readonly #onOutsideClick = (event: MouseEvent): void => {
    if (this.#isAnyOpen && !this.element.contains(event.target as Node)) {
      this.#closeAllMenus("outside");
    }
  };

  /**
   * Judges a click on a top item or command before the handlers inside the
   * menubar element run, and records the verdict for the `toggle` or `activate` the
   * click goes on to reach: refused when any entry holding the target refuses
   * activation, recorded with all of those entries. A click inside an
   * `aria-disabled` entry (itself or through an ancestor) never reaches those
   * handlers either: the ones on the entry and inside it can run before `toggle` and
   * `activate` refuse it, and a refusal does not stop the click.
   * Every entry that holds the target counts, so an `aria-disabled` entry nested in
   * an enabled one stops the click too. Native Enter/Space activation also
   * synthesizes a click and is blocked here; natively `disabled` buttons dispatch no
   * click at all.
   */
  readonly #onClickCapture = (event: MouseEvent): void => {
    const entries = ownersOf(this.#entries, event.target);
    this.#clickVerdicts.record(
      event,
      entries,
      entries.some((entry) => this.#isActivationDisabled(entry)),
    );
    if (!entries.some((entry) => isAriaDisabled(entry))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /**
   * Re-derives every piece of state this controller owns from the live targets:
   * expanded flags, menu visibility, the single Tab stop, and Escape-stack
   * membership. Runtime DOM edits can leave those out of step with each other — a
   * top can claim `aria-expanded="true"` after its menu target was removed or after
   * being hidden itself, a menu can stay visible after its owning top was removed,
   * an open pair can appear from a morph with no layer registered for it, and the
   * Tab stop can end up on a now-inert top, on a runtime-added one, or on none at
   * all. The only state carried between calls is the tracked focus record and which
   * side of the Escape stack this layer is on (which the stack itself does not expose),
   * so the outcome is the same whichever mutation arrived and calling it more often
   * than needed is free.
   */
  #reconcile(): void {
    for (const top of this.topTargets) {
      // A top is expanded only while its own menu resolves *and* is visible.
      // Both halves matter: the menu target can be removed outright, and a Turbo
      // Stream `replace` swaps it for a fresh (contract-`hidden`) one — which
      // still resolves, so a "did it disappear?" test alone would leave the top
      // claiming a popup nobody can see, and the Escape layer registered with it.
      // Only tops that already declare the attribute are touched: stamping
      // `aria-expanded` on a plain command would announce a popup it lacks.
      // The owner must also still be reachable: a consumer that `hidden`s or
      // natively disables an expanded top would otherwise leave its popup on
      // screen with no way back to the element that owns it.
      const own = this.#menuFor(top);
      if (this.#isExpanded(top) && (!own || own.hidden || !this.#isNavigable(top))) {
        top.setAttribute("aria-expanded", "false");
      }
    }
    for (const menu of this.menuTargets) {
      if (menu.hidden) continue;
      // A menu with no expanded owner can never be closed by the user again.
      const owner = this.#topFor(menu);
      if (!owner || !this.#isExpanded(owner)) menu.hidden = true;
    }
    this.#ensureTabStop();
    this.#rescueFocus();
    this.#syncEscapeLayer();
  }

  /**
   * Returns DOM focus to the menubar when the element holding it became
   * unreachable — hidden, natively disabled, or removed — and the document had
   * nowhere to put it. The destination is the top item that now owns the Tab
   * stop, which is where `Escape` from a closed menu would have left the user.
   *
   * Focus is only *restored*, never *stolen*: it moves solely when the tracked
   * element can no longer take it **and** focus is either already gone or still
   * sitting on that element. The second half is what makes a `hidden` ancestor
   * work — a browser blurs the element it hides on its next style pass, not when
   * the attribute is written, so waiting to observe `<body>` here would always
   * come too early. Removal is the other side of the same condition: the browser
   * has already fallen back to the body, or left a detached `activeElement`.
   */
  #rescueFocus(): void {
    const focused = this.#focused;
    if (!focused) return;
    // An ordinary close — Escape, Tab, an outside click — also leaves the focused
    // menu item inside a hidden subtree, but nothing went away and the user is
    // already on their way somewhere. Only an owner that can no longer be reached
    // is a reason to move focus, so the owner decides, not the item.
    const owner = this.topTargets.find(
      (top) => top === focused || this.#menuFor(top)?.contains(focused),
    );
    if (focused.isConnected && owner && this.#isNavigable(owner)) return;
    const doc = this.element.ownerDocument;
    const active = doc.activeElement;
    const lost =
      active === null ||
      active === doc.body ||
      active === doc.documentElement ||
      !active.isConnected ||
      active === focused ||
      focused.contains(active);
    if (!lost) return;
    const index = this.#roving.activeIndex;
    const top = index === -1 ? undefined : this.topTargets[index];
    if (top && top !== focused && !focused.contains(top)) top.focus();
  }

  /**
   * Brings Escape-stack membership back in line with what the DOM now says is
   * open, in both directions.
   *
   * The deactivate half is the common one (something closed). The activate half
   * covers a menu that became open *without* going through {@link #openMenu} —
   * a Turbo morph that patches `aria-expanded` and the menu's `hidden` in place,
   * which is legitimate here because the DOM is this controller's only source of
   * truth. Without it the popup is visible but `Escape` does nothing, since no
   * layer is registered to claim the press.
   *
   * `#layerActive` exists because re-activating an already-active layer moves it
   * to the top of the stack, which would reshuffle nested layers on every
   * unrelated mutation. Registering only on the false→true edge keeps activation
   * ordered by when each layer actually opened.
   */
  #syncEscapeLayer(): void {
    const open = this.#isAnyOpen;
    if (open === this.#layerActive) return;
    if (open) {
      this.#escapeLayer.activate(document, {
        onDismiss: () => this.#dismissOpenMenu(),
        claims: claimsWhileFocusWithin(this.element),
      });
    } else {
      this.#escapeLayer.deactivate();
    }
    this.#layerActive = open;
  }

  /**
   * Re-establishes the single Tab stop: keep the current one while it is still
   * navigable, else hand it to the first navigable top item. Every other top is
   * explicitly removed from the Tab sequence, so a top that arrived tabbable
   * cannot leave two stops behind. No Tab stop at all (`-1`) happens only when
   * every top is inert, and is recovered from as soon as one becomes navigable.
   */
  #ensureTabStop(): void {
    const active = this.#roving.activeIndex;
    const activeTop = active === -1 ? undefined : this.topTargets[active];
    if (activeTop && this.#isNavigable(activeTop)) {
      this.#roving.setActive(active);
      return;
    }
    const first = this.#navigableTops[0];
    this.#roving.setActive(first ? this.topTargets.indexOf(first) : -1);
  }

  /** The menu element controlled by `top` (matched by `aria-controls`/`id`). */
  #menuFor(top: HTMLElement): HTMLElement | null {
    const id = top.getAttribute("aria-controls");
    // Resolve against this controller's own menu targets (not a global id lookup)
    // so it stays scoped to this menubar instance.
    return id ? (this.menuTargets.find((menu) => menu.id === id) ?? null) : null;
  }

  /** The top item that controls `menu` (reverse of `#menuFor`). */
  #topFor(menu: HTMLElement | null): HTMLButtonElement | null {
    if (!menu) return null;
    return this.topTargets.find((top) => top.getAttribute("aria-controls") === menu.id) ?? null;
  }

  /** The navigable item targets that live inside `menu`, in DOM order. */
  #itemsIn(menu: HTMLElement): HTMLButtonElement[] {
    return this.itemTargets.filter((item) => menu.contains(item) && this.#isNavigable(item));
  }

  /** Every top item and command: the entries a click or an action can land on. */
  get #entries(): HTMLButtonElement[] {
    return [...this.topTargets, ...this.itemTargets];
  }

  /** Top items eligible for the roving tab stop and the arrow keys. */
  get #navigableTops(): HTMLButtonElement[] {
    return this.topTargets.filter((top) => this.#isNavigable(top));
  }

  /**
   * Whether `element` can take roving focus: `hidden` and natively `disabled`
   * controls are out of reach and are skipped. `aria-disabled` is deliberately
   * **not** checked — the APG keeps such items focusable so they stay
   * discoverable, and {@link MenubarController.#isActivationDisabled} is what
   * suppresses acting on them. CSS-only visibility is not detectable headlessly
   * and stays the consumer's responsibility.
   */
  #isNavigable(element: HTMLButtonElement): boolean {
    if (element.hasAttribute("hidden")) return false;
    return !element.disabled;
  }

  /**
   * Whether `element` refuses activation: it is out of reach (its own `hidden` or
   * native `disabled`) or `aria-disabled`, itself or through an ancestor, which
   * keeps it reachable but never activated.
   */
  #isActivationDisabled(element: HTMLButtonElement): boolean {
    return !this.#isNavigable(element) || isAriaDisabled(element);
  }

  /**
   * Whether `toggle` or `activate` refuses `entry` for `event`, or for the explicit
   * element `origin` when there is no event. A click keeps the verdict its capture
   * pass took, before the handlers inside the menubar element could change it,
   * when `entry` was among the entries holding its target. Otherwise — any other
   * event, an explicit element, or a click recorded without `entry` — `entry` itself
   * and the entries holding the event's target, or the element, are judged now, and
   * any one that refuses activation refuses `entry`. `entry` is judged on its own
   * because a handler can move the target out of it before the action runs.
   */
  #refuses(entry: HTMLButtonElement, event: Event | null, origin: Element | null): boolean {
    const recorded = this.#clickVerdicts.for(event, entry);
    if (recorded !== undefined) return recorded;
    if (this.#isActivationDisabled(entry)) return true;
    const target = event ? event.target : origin;
    return ownersOf(this.#entries, target).some((owner) => this.#isActivationDisabled(owner));
  }

  /** Moves DOM focus to the item at `index` (no-op if out of range). */
  #focusAt(items: HTMLElement[], index: number): void {
    items[index]?.focus();
  }

  /** Returns roving focus to a top item (and makes it the single tab stop). */
  #focusTop(top: HTMLButtonElement | null, focus = true): void {
    if (!top) return;
    this.#roving.setActive(this.topTargets.indexOf(top), { focus });
  }

  /** Whether `top`'s menu is currently expanded. */
  #isExpanded(top: HTMLElement): boolean {
    return top.getAttribute("aria-expanded") === "true";
  }

  /** Whether any top item's menu is currently open. */
  get #isAnyOpen(): boolean {
    return this.topTargets.some((top) => this.#isExpanded(top));
  }

  /** Advances focus to the next item in `items` matching the accumulated query. */
  #typeaheadTo(items: HTMLElement[], current: number, key: string): void {
    const index = findTypeaheadMatch(items, current, this.#typeahead.push(key));
    if (index !== -1) items[index]?.focus();
  }
}
